import { st } from "~/sassmall/i18n";
import {
  getSassmallResponseMessage,
  isSassmallResponseSuccess,
  sassmallRequest,
} from './client'
import {
  listSassmallLogisticsRouteDestinations,
  listSassmallWarehouseOptions,
} from './warehouse'

export interface SassmallGoodsPostCalRequest {
  warehouseCode?: string
  countryCode: string
  cargoTypeCode?: string
  goodType?: string
  goodCode?: string
  weight?: number | string
  bizType?: string
  bizTypeList?: string[]
  productName?: string
  category?: string
  skuText?: string
  skuInfo?: Array<Record<string, any>>
}

export interface SassmallGoodsPostRoute {
  id?: string | number | null
  warehouseCode?: string | null
  countryCode?: string | null
  cargoType?: string | null
  freightType?: string | null
  weight?: number | null
  price?: number | null
  timeScope?: string | null
  productName?: string | null
  carrierList?: string[] | null
  isAllow?: number | null
  [key: string]: any
}

export interface SassmallCheckSendCountry {
  code: string
  value: string
  aliasName: string
  country: string
}

function isCompatibilityResponseSuccess(response: any) {
  return isSassmallResponseSuccess(response) || String(response?.status || '').toUpperCase() === 'SUCCESS'
}

/**
 * 商品是否可寄统一走 Customer/SassMall 兼容接口，由 SassMall client 注入租户和登录态。
 * 新请求同时支持商品名称、品类及 SKU 文本，后端可据此进行商品分类识别。
 */
export async function checkSassmallGoodsPostable(payload: SassmallGoodsPostCalRequest) {
  const response = await sassmallRequest<SassmallGoodsPostRoute[]>('/dealerShopServiceProduct/goodsPostCalList', {
    method: 'POST',
    body: payload,
  })

  if (!isCompatibilityResponseSuccess(response)) {
    throw new Error(getSassmallResponseMessage(response, st("商品是否可寄查询失败")))
  }

  return Array.isArray(response.data) ? response.data : []
}

/**
 * 新系统不再提供旧 queryCountryList；目的地来自租户启用的仓库物流线路。
 * 未指定仓库时合并所有可用仓库的目的地，商品详情和独立查询页可共用。
 */
function countryDisplayName(code: string, locale = '') {
  try {
    const DisplayNames = (Intl as any).DisplayNames
    return DisplayNames
      ? new DisplayNames([locale || 'zh-CN'], { type: 'region' }).of(code.toUpperCase()) || code
      : code
  }
  catch {
    return code
  }
}

export async function listSassmallCheckSendCountries(
  warehouseCode = '',
  locale = '',
): Promise<SassmallCheckSendCountry[]> {
  const normalizedWarehouseCode = String(warehouseCode || '').trim()
  let warehouseCodes = normalizedWarehouseCode ? [normalizedWarehouseCode] : []

  if (!warehouseCodes.length) {
    const warehouses = await listSassmallWarehouseOptions()
    warehouseCodes = warehouses
      .map(item => String(item?.warehouseCode || '').trim())
      .filter(Boolean)
  }

  const destinationLists = await Promise.all(
    [...new Set(warehouseCodes)].map(code => listSassmallLogisticsRouteDestinations(code)),
  )
  const countryCodes = [...new Set(destinationLists.flat().map(code => String(code || '').trim()).filter(Boolean))]

  return countryCodes.map(code => ({
    code,
    value: code,
    aliasName: countryDisplayName(code, locale),
    country: code,
  }))
}
